import { boardFromFen, boardToFen, findGeneral } from './engine';
import { DIFFICULTY_ORDER, emptyOpponentRecords } from './gameplay';
import type { BoardStyle, Difficulty, GameMode, GameState, OpponentRecord, PlayerProfile, Side } from './types';

const AUTOSAVE_KEY = 'cloud-xiangqi:autosave:v1';
const MANUAL_SAVES_KEY = 'cloud-xiangqi:manual-saves:v1';
const PROFILE_KEY = 'cloud-xiangqi:profile:v1';

export interface StoredGameV1 {
  schemaVersion: 1;
  name: string;
  savedAt: string;
  difficulty: Difficulty;
  state: Omit<GameState, 'board'> & { board: string };
}

export interface StoredGameV2 {
  schemaVersion: 2;
  name: string;
  savedAt: string;
  difficulty: Difficulty;
  boardStyle: BoardStyle;
  state: Omit<GameState, 'board'> & { board: string };
}

export interface StoredGameV3 {
  schemaVersion: 3;
  name: string;
  savedAt: string;
  difficulty: Difficulty;
  boardStyle: BoardStyle;
  gameMode: GameMode;
  undosUsed: number;
  state: Omit<GameState, 'board'> & { board: string };
}

export interface StoredGameV4 {
  schemaVersion: 4;
  name: string;
  savedAt: string;
  difficulty: Difficulty;
  boardStyle: BoardStyle;
  gameMode: GameMode;
  undosUsed: number;
  playerSide: Side;
  state: Omit<GameState, 'board'> & { board: string };
}

export type StoredGame = StoredGameV1 | StoredGameV2 | StoredGameV3 | StoredGameV4;

export interface NamedSave {
  id: string;
  data: StoredGame;
}

const DEFAULT_PROFILE: PlayerProfile = {
  score: 0,
  scoreBasis: 'zero',
  wins: 0,
  draws: 0,
  losses: 0,
  masterWins: 0,
  recordedGameIds: [],
  opponentRecords: emptyOpponentRecords(),
};

function hasStorage(): boolean {
  return typeof localStorage !== 'undefined';
}

export function serializeGame(
  state: GameState,
  difficulty: Difficulty,
  boardStyle: BoardStyle = '2d',
  name = '未命名棋局',
  gameMode: GameMode = 'practice',
  undosUsed = 0,
  playerSide: Side = 'red',
): StoredGameV4 {
  return {
    schemaVersion: 4,
    name: name.trim().slice(0, 40) || '未命名棋局',
    savedAt: new Date().toISOString(),
    difficulty,
    boardStyle,
    gameMode,
    undosUsed: Math.max(0, Math.floor(undosUsed)),
    playerSide,
    state: { ...state, board: boardToFen(state.board) },
  };
}

function assertDifficulty(value: unknown): asserts value is Difficulty {
  if (!DIFFICULTY_ORDER.includes(String(value) as Difficulty)) throw new Error('未知的對手類型');
}

function assertBoardStyle(value: unknown): asserts value is BoardStyle {
  if (value !== '2d' && value !== '3d') throw new Error('未知的棋盤樣式');
}

function assertGameMode(value: unknown): asserts value is GameMode {
  if (value !== 'practice' && value !== 'challenge') throw new Error('不支援的對局模式');
}

export function deserializeGame(input: unknown): {
  state: GameState;
  difficulty: Difficulty;
  boardStyle: BoardStyle;
  gameMode: GameMode;
  undosUsed: number;
  playerSide: Side;
  name: string;
  savedAt: string;
} {
  if (!input || typeof input !== 'object') throw new Error('存檔不是有效物件');
  const value = input as Partial<StoredGame>;
  if (![1, 2, 3, 4].includes(Number(value.schemaVersion))) throw new Error('不支援這個存檔版本');
  assertDifficulty(value.difficulty);
  const boardStyle: BoardStyle = value.schemaVersion === 1 ? '2d' : (value as Partial<StoredGameV2>).boardStyle as BoardStyle;
  assertBoardStyle(boardStyle);
  const gameMode: GameMode = value.schemaVersion === 3 || value.schemaVersion === 4 ? value.gameMode as GameMode : 'practice';
  assertGameMode(gameMode);
  const undosUsed = value.schemaVersion === 3 || value.schemaVersion === 4
    ? Math.max(0, Math.floor(Number(value.undosUsed) || 0))
    : 0;
  const playerSide: Side = value.schemaVersion === 4 && (value as Partial<StoredGameV4>).playerSide === 'black' ? 'black' : 'red';
  if (!value.state || typeof value.state !== 'object' || typeof value.state.board !== 'string') throw new Error('存檔缺少棋盤');
  const board = boardFromFen(value.state.board);
  if (findGeneral(board, 'red') < 0 || findGeneral(board, 'black') < 0) throw new Error('存檔缺少將或帥');
  const sideToMove = value.state.sideToMove;
  if (sideToMove !== 'red' && sideToMove !== 'black') throw new Error('存檔的行棋方錯誤');
  if (!Array.isArray(value.state.moves) || !value.state.positionCounts || typeof value.state.positionCounts !== 'object') throw new Error('存檔歷史資料錯誤');
  const state: GameState = {
    ...value.state,
    board,
    gameId: String(value.state.gameId || crypto.randomUUID()),
    seed: Number(value.state.seed) >>> 0,
    noCapturePly: Number(value.state.noCapturePly) || 0,
    outcome: ['ongoing', 'red_win', 'black_win', 'draw'].includes(value.state.outcome) ? value.state.outcome : 'ongoing',
    outcomeReason: String(value.state.outcomeReason || ''),
  } as GameState;
  return {
    state,
    difficulty: value.difficulty,
    boardStyle,
    gameMode,
    undosUsed,
    playerSide,
    name: String(value.name || '未命名棋局'),
    savedAt: String(value.savedAt || ''),
  };
}

export function saveAutosave(
  state: GameState,
  difficulty: Difficulty,
  boardStyle: BoardStyle = '2d',
  gameMode: GameMode = 'practice',
  undosUsed = 0,
  playerSide: Side = 'red',
): void {
  if (!hasStorage()) return;
  localStorage.setItem(AUTOSAVE_KEY, JSON.stringify(serializeGame(state, difficulty, boardStyle, '自動存檔', gameMode, undosUsed, playerSide)));
}

export function loadAutosave(): ReturnType<typeof deserializeGame> | null {
  if (!hasStorage()) return null;
  const raw = localStorage.getItem(AUTOSAVE_KEY);
  if (!raw) return null;
  try {
    return deserializeGame(JSON.parse(raw));
  } catch {
    localStorage.removeItem(AUTOSAVE_KEY);
    return null;
  }
}

export function listNamedSaves(): NamedSave[] {
  if (!hasStorage()) return [];
  try {
    const value = JSON.parse(localStorage.getItem(MANUAL_SAVES_KEY) || '[]') as NamedSave[];
    return Array.isArray(value) ? value : [];
  } catch {
    return [];
  }
}

export function putNamedSave(
  state: GameState,
  difficulty: Difficulty,
  boardStyle: BoardStyle,
  name: string,
  gameMode: GameMode = 'practice',
  undosUsed = 0,
  playerSide: Side = 'red',
): NamedSave[] {
  const data = serializeGame(state, difficulty, boardStyle, name, gameMode, undosUsed, playerSide);
  const save: NamedSave = { id: `${state.gameId}:${Date.now()}`, data };
  const next = [save, ...listNamedSaves()].slice(0, 12);
  if (hasStorage()) localStorage.setItem(MANUAL_SAVES_KEY, JSON.stringify(next));
  return next;
}

export function deleteNamedSave(id: string): NamedSave[] {
  const next = listNamedSaves().filter((save) => save.id !== id);
  if (hasStorage()) localStorage.setItem(MANUAL_SAVES_KEY, JSON.stringify(next));
  return next;
}

function toCount(value: unknown): number {
  return Math.max(0, Math.floor(Number(value) || 0));
}

export function loadProfile(): PlayerProfile {
  if (!hasStorage()) return { ...DEFAULT_PROFILE, opponentRecords: emptyOpponentRecords() };
  try {
    const value = JSON.parse(localStorage.getItem(PROFILE_KEY) || '{}') as Partial<PlayerProfile>;
    const sourceRecords = value.opponentRecords as Partial<Record<Difficulty, Partial<OpponentRecord>>> | undefined;
    const opponentRecords = emptyOpponentRecords();
    for (const id of DIFFICULTY_ORDER) {
      const record = sourceRecords?.[id];
      opponentRecords[id] = {
        wins: toCount(record?.wins),
        draws: toCount(record?.draws),
        losses: toCount(record?.losses),
      };
    }
    const score = Number(value.score);
    const migratedScore = Number.isFinite(score)
      ? value.scoreBasis === 'zero' ? score : score - 6_000
      : DEFAULT_PROFILE.score;
    return {
      score: Math.max(0, Math.floor(migratedScore)),
      scoreBasis: 'zero',
      wins: toCount(value.wins),
      draws: toCount(value.draws),
      losses: toCount(value.losses),
      masterWins: toCount(value.masterWins),
      recordedGameIds: Array.isArray(value.recordedGameIds) ? value.recordedGameIds.map(String).slice(-100) : [],
      opponentRecords,
    };
  } catch {
    return { ...DEFAULT_PROFILE, opponentRecords: emptyOpponentRecords() };
  }
}

export function saveProfile(profile: PlayerProfile): void {
  if (hasStorage()) localStorage.setItem(PROFILE_KEY, JSON.stringify(profile));
}
